import { Region } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiSelect } from '@experiences/ui-common';
import React, { useEffect } from 'react';
import {
    Controller,
    useFormContext,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import useEcommerceDataStorageRegionViewModel from './useEcommerceDataStorageRegionViewModel';

export const EcommerceDataStorageRegion: React.FC<{
    disabled: boolean;
}> = ({ disabled }) => {
    const { formatMessage: translate } = useIntl();
    const {
        regions, control, errors,
    } = useEcommerceDataStorageRegionViewModel();
    const {
        setValue, getValues,
    } = useFormContext();
    const enableEcommercePricingV2 = useFeatureFlagValue(Features.EnableEcommercePricingV2.name);

    // Default to EUROPE because non-enterprise users should not have this option, but never leave the
    // form on a region the response did not advertise.
    useEffect(() => {
        if (!regions || getValues('region') in regions) {
            return;
        }

        const [ firstRegion ] = Object.keys(regions);
        setValue('region', (Region.Europe in regions ? Region.Europe : firstRegion) as Region);
    }, [ regions, getValues, setValue ]);

    return (
        <Controller
            control={control}
            rules={{
                required: true,
                validate: p => !!p?.trim(),
            }}
            name="region"
            defaultValue={Region.Europe}
            render={({ field }) => (
                <UiSelect
                    {...field}
                    id="region"
                    name="region"
                    control={control}
                    disabled={disabled || enableEcommercePricingV2}
                    inputLabel={translate({ id: 'CLIENT_DATA_STORAGE_REGION' })}
                    options={regions ?? []}
                    error={!!errors.region}
                    helperText={
                        errors.logicalName?.type === 'required' &&
                    translate(
                        { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                        { 0: translate({ id: 'CLIENT_DATA_STORAGE_REGION' }) },
                    )
                    }
                    required
                    fullWidth
                    dataCy="ecommerce-form-data-region"
                />
            )}
        />
    );
};
